import config from 'config';
import { EMPTY_ARRAY } from 'constants/defaults';
import { HOUR_EDITOR_MAX_PINNED_PROJECTS } from 'constants/hour';
import { MINIMUM_PROJECTS_ON_DASHBOARD } from 'constants/project';
import { PREFERRED_PROJECTS_TAB } from 'constants/user';
import {
  clientWithProjectsAsDropdownOptions,
  enrichProjectWithBudgetAttributes,
  makeEmptyEnrichedProject,
  tagListFactory,
} from 'factories/projects';
import { isEqual } from 'lodash-es';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import {
  getAccountCurrency,
  getAccountFeatures,
  getAllProjectsLoggingEnabled,
  getCurrentAccount,
} from 'redux/selectors/accounts';
import { getAppsWithProjectImportSupport } from 'redux/selectors/apps';
import { getActiveClients, getArchivedClients } from 'redux/selectors/clients';
import { getPinnedProjectIds, getPinnedProjectIdsSet } from 'redux/selectors/pins';
import { getRouteSearchAsObject } from 'redux/selectors/routing';
import { getIsUserAdmin, getUserPermissions, getUserProperties } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Client } from 'types/client';
import type { EnrichedProject, EnrichedProjectWithBudget, FilterGroupValues } from 'types/project';
import type { ClientWithProjects } from 'utils/project';
import {
  getActiveProjects,
  getAvailableProjectsForUser,
  groupProjectsByClient,
} from 'utils/project';
import { keyBy, notNull } from 'utils/typesafe';
import {
  enrichProjectVariant,
  enrichProjectVariantOrNull,
  projectSelectorFilterProjectIds,
} from './utils/projects';

const readProjectsState = (state: IState) => state.projects;
const getClients = (state: IState) => state.clients.entries;
const readProjectById = (state: IState) => state.projects.projectById;
export const readProjectIds = (state: IState) => state.projects.projectIds;
const includeArchivedInGetProjectsForUser = (
  state: IState,
  props: unknown,
  includeArchived: boolean
) => includeArchived !== false;
const readDraft = (state: IState) => state.projects.draft;
const readProjectFilter = (state: IState) => state.projects.filter;
const getAuthenticatedUserId = (state: IState) => state.user.id;
export const getProjectPermissionsMap = (state: IState) =>
  state.projects.projectPermissionsByUserId;

const readXhrStatus = (state: IState) => state.projects.xhrStatus;

const getProjectPagination = (state: IState) => state.projects.paginatedView;

// circular deps 😕
const readTags = (state: IState) => state.tags.entities.tag;
const getTagEntities = readTags;

export const getProjectById = readProjectById;
export const getProjectFilter = readProjectFilter;
export const getXhrStatus = readXhrStatus;

export const getProjectsFetchingStatus = createSelector(
  [readProjectsState],
  state => state.projectFetchingStatus
);

export const getInitialProjectsHaveBeenFetched = createSelector(
  [getProjectsFetchingStatus],
  projectFetchingStatus => projectFetchingStatus !== 'lazy'
);
export const getAllProjectsHaveBeenFetched = createSelector(
  [getProjectsFetchingStatus],
  projectFetchingStatus =>
    projectFetchingStatus === 'fetchedAllFull' || projectFetchingStatus === 'fetchedAllLight'
);

export const getProjectXhrStatus = createSelector([getXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);
export const getIsLoadingProjects = createSelector(
  [getProjectXhrStatus],
  xhrStatus => xhrStatus.isLoading
);

export const hasProjectDraft = createSelector(
  [readDraft],
  (draft): boolean => draft !== null && Object.keys(draft).length > 0
);

export const getIsLoadingPaginatedProjects = createSelector([getXhrStatus], xhrStatus => {
  const state = xhrStatusUIState(xhrStatus, 'projectList');
  return !state.hasInitialized || state.isLoading;
});

export const getDraft = readDraft;

export const getProjectDraftStatus = createSelector([hasProjectDraft], exists => {
  return {
    exists, // Flesh out as needed, see getHourDraftStatus for inspiration? Standardize a type?
  };
});

export const getDraftLabels = createSelector([getDraft], draft =>
  ((draft && draft.labels) || []).map(tag => ({
    ...tag,
    // We store the required status in required_label_ids during editing
    required: draft?.required_label_ids?.includes(tag.label_id) === true,
  }))
);

export const getDraftLabelIds = createSelector([getDraftLabels], labels => {
  return labels.map(label => label.label_id);
});

export const getDraftLabelsLookup = createSelector([getDraftLabels], labels =>
  keyBy(labels, 'label_id')
);

export interface ActAsUserIdOwnProps {
  actAsUserId?: number | null;
  initialProjectId?: number | null;
  hideArchivedProjects?: boolean;
  filterGroup?: FilterGroupValues;
}

const readActAsUserProps = (_: unknown, props: ActAsUserIdOwnProps) => {
  return {
    actAsUserId: props?.actAsUserId || null,
    initialProjectId: props?.initialProjectId || null,
    hideArchivedProjects: props?.hideArchivedProjects || false,
    filterGroup: props?.filterGroup || 'all',
  };
};

const getActAsUserProps = createSelector([readActAsUserProps], actAsUserProps => actAsUserProps, {
  memoizeOptions: { resultEqualityCheck: isEqual },
});

export const getActAsUserId = createSelector(
  [readActAsUserProps],
  actAsUserProps => actAsUserProps.actAsUserId
);

export const getUserId = createSelector(
  [getAuthenticatedUserId, getActAsUserId],
  (authUserId, actAsUserId) => actAsUserId || authUserId
);

const getAuthenticatedUserProjectPermissions = createSelector(
  [getProjectPermissionsMap, getAuthenticatedUserId],
  (permissionsMap, userId) => permissionsMap[userId] ?? EMPTY_ARRAY
);

const getActingAsUserProjectPermissions = createSelector(
  [getProjectPermissionsMap, getUserId],
  (permissionsMap, userId) => permissionsMap[userId] ?? EMPTY_ARRAY
);

export const getAllLoadedProjects = createSelector(
  [readProjectIds, readProjectById, getPinnedProjectIdsSet],
  (projectIds, projectById, pinnedProjectIdsSet): Array<EnrichedProject> => {
    return projectIds
      .map(id => {
        return enrichProjectVariantOrNull(projectById[id], pinnedProjectIdsSet);
      })
      .filter(notNull);
  }
);

export const getProjectVariantMap = readProjectById;

/**
 * Returns the 10 most recent project ids used by the authenticated user
 * Ignores deleted projects, archived projects & projects not in local store.
 */
export const getRecentlyUsedProjectIds = createSelector(
  [getAuthenticatedUserProjectPermissions, getProjectVariantMap],
  (permissions, projectVariantMap): Array<number> =>
    projectSelectorFilterProjectIds(permissions, projectVariantMap),
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export const getRecentlyUsedProjects = createSelector(
  [getRecentlyUsedProjectIds, getProjectVariantMap, getPinnedProjectIdsSet],
  (ids, variantMap, pinnedProjectIdsSet): Array<EnrichedProject> =>
    ids.map(id => enrichProjectVariantOrNull(variantMap[id], pinnedProjectIdsSet)).filter(notNull)
);

/**
 * Returns a set of pinned project ids,
 * limiting the number of pinned projects to HOUR_EDITOR_MAX_PINNED_PROJECTS
 * and preferring recently used projects.
 */
export const getRecentlyUsedPinnedProjectIdsSet = createSelector(
  [getRecentlyUsedProjectIds, getPinnedProjectIdsSet],
  (recentlyUsedProjectIds, pinnedProjectIdsSet): Set<number> => {
    if (pinnedProjectIdsSet.size > HOUR_EDITOR_MAX_PINNED_PROJECTS) {
      const recentPinned = recentlyUsedProjectIds.filter(id => pinnedProjectIdsSet.has(id));

      const remainingCount = HOUR_EDITOR_MAX_PINNED_PROJECTS - recentPinned.length;
      if (remainingCount > 0) {
        const otherPinned = Array.from(pinnedProjectIdsSet)
          .filter(id => !recentlyUsedProjectIds.includes(id))
          .slice(0, remainingCount);
        return new Set([...recentPinned, ...otherPinned]);
      }
      return new Set(recentPinned.slice(0, HOUR_EDITOR_MAX_PINNED_PROJECTS));
    }
    return pinnedProjectIdsSet;
  }
);

export const getPinnedProjectsForProjectDropdown = createSelector(
  [getRecentlyUsedPinnedProjectIdsSet, getProjectVariantMap],
  (pinnedProjectIdsSet, projectById): Array<EnrichedProject> => {
    return Array.from(pinnedProjectIdsSet)
      .map(projectId => {
        return enrichProjectVariantOrNull(projectById[projectId], pinnedProjectIdsSet);
      })
      .filter(notNull);
  }
);

export const getAllActiveProjects = createSelector(
  [getAllLoadedProjects, getClients],
  (projects, clients) => {
    const archivedClientIds = clients.filter(client => !client.active).map(client => client.id);

    return getActiveProjects(projects, archivedClientIds);
  }
);

export const getHasReachedProjectLimit = createSelector(
  [getAllActiveProjects, getCurrentAccount],
  (projects, account) => {
    return account.max_projects !== 0 && projects.length >= account.max_projects;
  }
);

export const getAllProjectsGroupedByClient = createSelector(
  [getActiveClients, getArchivedClients, getAllLoadedProjects],
  (activeClients, archivedClients, projects) =>
    groupProjectsByClient(projects, [...activeClients, ...archivedClients])
);

const getPaginatedProjects = createSelector(
  [getProjectPagination, getProjectVariantMap, getPinnedProjectIdsSet],
  (pagination, projectMap, pinnedProjectIdsSet): Array<EnrichedProject> => {
    return (pagination?.projectIds ?? [])
      .map(id => projectMap[id])
      .filter(notNull)
      .map(project => enrichProjectVariant(project, pinnedProjectIdsSet));
  }
);

export const getAllPinnedAndRecentProjects = createSelector(
  [getAllLoadedProjects, getPinnedProjectIds, getAuthenticatedUserProjectPermissions],
  (projects, pinnedProjectIds, projectPermissions): Array<EnrichedProject> => {
    const activeProjectsById = keyBy(
      projects.filter(project => project.base.active),
      'base.id'
    );

    const getActiveProjectsByIds = (ids: Array<number>): Array<EnrichedProject> => {
      return ids.map(id => activeProjectsById[id]).filter(notNull);
    };

    const pinnedProjects = getActiveProjectsByIds(pinnedProjectIds);

    // Number of recent projects we need (in case pinned projects is less than MINIMUM_PROJECTS_ON_DASHBOARD)
    const numRecentCardsRequired =
      pinnedProjects.length >= MINIMUM_PROJECTS_ON_DASHBOARD
        ? 0
        : MINIMUM_PROJECTS_ON_DASHBOARD - pinnedProjects.length;
    const filteredPermissions =
      numRecentCardsRequired > 0
        ? projectPermissions.filter(
            p => !pinnedProjectIds.includes(p) && activeProjectsById[p] != null
          )
        : [];

    // merge recent projects with recent project permissions, allowing us to orderBy 'logged_at' and 'name'
    const recentlyUpdatedProjects = filteredPermissions
      .slice(0, numRecentCardsRequired)
      .map(
        filteredProjectPermission =>
          activeProjectsById[filteredProjectPermission] ?? makeEmptyEnrichedProject()
      );

    return [...pinnedProjects, ...recentlyUpdatedProjects];
  }
);

export const projectsForUser = createSelector(
  [
    getAllLoadedProjects,
    getClients,
    includeArchivedInGetProjectsForUser,
    getActingAsUserProjectPermissions,
  ],
  (projects, clients, includeArchived, permissions) => {
    const archivedClientIds = clients.filter(client => !client.active).map(client => client.id);
    return getAvailableProjectsForUser(projects, includeArchived, archivedClientIds, permissions);
  }
);

/**
 * Returns EnrichedProject's the user is allowed to use.
 */
export const getEnrichedProjectsForCurrentUser = createSelector(
  [(state: IState) => projectsForUser(state, { actAsUserId: null }, false)],
  (projects): Array<EnrichedProject> => projects
);

export const getEnrichedProjectsForCurrentUserById = createSelector(
  [getEnrichedProjectsForCurrentUser],
  (projects): Record<number, EnrichedProject> => keyBy(projects, p => p.base.id)
);

export const projectCountForCurrentUser = createSelector(
  [(state: IState) => projectsForUser(state, { actAsUserId: null }, false)],
  projects => {
    return projects.length;
  }
);

const getPaginatedProjectsByClient = createSelector([getPaginatedProjects], projects => {
  type Result = Array<ClientWithProjects<EnrichedProjectWithBudget>>;
  return projects.reduce((result: Result, project): Result => {
    let slot = result.find(x => x.client.id === project.base.client.id);
    if (!slot) {
      slot = { client: project.base.client, projects: [] };
      result.push(slot);
    }
    const enrichedProject = enrichProjectWithBudgetAttributes(project);
    slot.projects.push(enrichedProject);

    return result;
  }, []);
});

interface FlatClientOrProjectHeader extends Client {
  header: true;
}
export interface FlatClientOrProjectProject extends EnrichedProjectWithBudget {
  header?: false;
  showClient?: boolean;
}
export type FlatClientOrProject = FlatClientOrProjectHeader | FlatClientOrProjectProject;
export const getProjectsByClientFlatArray = createSelector(
  [getProjectFilter, getPaginatedProjectsByClient, getPaginatedProjects, getAccountCurrency],
  (filter, clientGroups, flatProjects): Array<FlatClientOrProject> => {
    // We can only group by client when we have sorted by clientName
    if (filter.sortBy.field !== 'client_name') {
      return flatProjects.map(
        (project): FlatClientOrProjectProject => ({
          ...enrichProjectWithBudgetAttributes(project),
          showClient: true,
        })
      );
    }
    return clientGroups.reduce((list: Array<FlatClientOrProject>, current) => {
      const { projects, client } = current;
      const header = {
        header: true as const,
        ...client,
      };
      list.push(header);
      list.push(...projects);
      return list;
    }, []);
  }
);

export const getProjectTags = createSelector(
  [getDraftLabelIds, getTagEntities],
  (draftLabelIds = [], tagEntities) => {
    if (!draftLabelIds.length) {
      return [];
    }

    if (draftLabelIds.length) {
      return draftLabelIds.map(id => tagListFactory(+id, tagEntities[id]));
    }

    return (
      (tagEntities &&
        Object.keys(tagEntities).map(id => tagListFactory(+id, tagEntities[Number(id)]))) ||
      []
    );
  }
);

export const getProjectRoutePage = createSelector(
  [
    getUserPermissions,
    getHasReachedProjectLimit,
    getAccountFeatures,
    getAppsWithProjectImportSupport,
    projectCountForCurrentUser,
  ],
  (
    permissions,
    isProjectLimitReached,
    features,
    appsWithProjectImportSupport,
    numberOfProjects
  ) => {
    return {
      appsWithProjectImportSupport,
      isProjectLimitReached,
      permissions,
      isProjectsDashboardFeatureLocked: !features.project_dashboard,
      numberOfProjects,
    };
  }
);

export const makeGetProjectSearchOptions = () =>
  createSelector(
    [
      getAllProjectsGroupedByClient,
      getCurrentAccount,
      getActingAsUserProjectPermissions,
      getAuthenticatedUserProjectPermissions,
      getAllProjectsHaveBeenFetched,
      getActAsUserProps,
      getAllProjectsLoggingEnabled,
      getIsUserAdmin,
    ],
    (
      projectsByClient,
      account,
      actingPermissions,
      authenticatedPermissions,
      allProjectsLoaded,
      { actAsUserId, initialProjectId, hideArchivedProjects, filterGroup },
      allProjectsLogging,
      isAdmin
    ) => {
      let projectIdWhitelist =
        actAsUserId && actingPermissions.length ? actingPermissions.slice() : null;
      // When ALL_PROJECTS_LOGGING is on, the hour editor preloads every
      // workspace project into Redux. Keep the Projects-page search bar
      // scoped to the current user's assigned projects so it does not
      // inherit that broader list. Also handle the zero-permissions case
      // (whitelist of []) — without the feature flag we leave whitelist
      // null so existing behavior for self-view is preserved.
      if (!projectIdWhitelist && !isAdmin && allProjectsLogging) {
        projectIdWhitelist = authenticatedPermissions.slice();
      }
      if (projectIdWhitelist && initialProjectId) {
        projectIdWhitelist.push(initialProjectId);
      }
      return {
        clientProjectOptionsGroup: clientWithProjectsAsDropdownOptions(projectsByClient, {
          currencySymbol: account.currency.symbol,
          projectIdWhitelist,
          hideArchivedProjects,
          filterGroup,
        }),
        allProjectsLoaded,
      };
    }
  );

export const getProjectsIndexUIstate = createSelector(
  [getProjectsByClientFlatArray, getProjectXhrStatus],
  (flatClientProjectList, { isLoading }) => {
    const hasProjects = flatClientProjectList.length > 0;
    const showEmptyState = !hasProjects && !isLoading;
    const showLoadingState = !hasProjects && isLoading;

    return { hasProjects, showEmptyState, showLoadingState, isLoading };
  }
);

/**
 * Do filters set in URL mismatch what's in the store?
 * See ProjectsIndexRoute::UNSAFE_componentWillReceiveProps for usage
 */
export const getFilterMismatchUrlCheck = createSelector(
  [getProjectFilter, getRouteSearchAsObject],
  (filter, urlFilter) => {
    const hasStatusMismatch =
      !!urlFilter.projectStatus && urlFilter.projectStatus !== filter.projectStatus;
    const budgetStatusMismatch =
      !!urlFilter.budgetStatus && urlFilter.budgetStatus !== filter.budgetStatus;
    const fieldMismatch = !!urlFilter.field && urlFilter.field !== filter.sortBy.field;
    const directionMismatch =
      !!urlFilter.direction && urlFilter.direction !== filter.sortBy.direction;

    return hasStatusMismatch || budgetStatusMismatch || fieldMismatch || directionMismatch;
  }
);

export interface PaginationMeta {
  initialized: boolean;
  isLoading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  hasPreviousPage: boolean;
  couldHaveNextPage: boolean;
}
export const getProjectPaginationMeta = createSelector(
  [getProjectPagination, getIsLoadingPaginatedProjects],
  (pagination, isLoading): PaginationMeta => {
    return {
      initialized: pagination != null,
      isLoading,
      page: pagination?.page ?? 1,
      pageSize: config.projects.projectsPerPage,
      totalCount: pagination?.totalCount ?? 0,
      hasPreviousPage: (pagination?.page ?? 1) > 1,
      couldHaveNextPage:
        pagination?.totalCount != null
          ? pagination.page * config.projects.projectsPerPage < pagination.totalCount
          : pagination != null && pagination.projectIds.length >= config.projects.projectsPerPage,
    };
  }
);

export const getPreferredProjectsView = createSelector(
  [getUserProperties, getAccountFeatures],
  (properties, features) => {
    if (!features.project_dashboard) {
      return 'list';
    }

    return properties[PREFERRED_PROJECTS_TAB] == null ||
      properties[PREFERRED_PROJECTS_TAB] === 'dashboard'
      ? 'dashboard'
      : 'list';
  }
);

/**
 * Returns the total number of projects in the workspace available to the current user.
 */
export const getTotalProjectsCount = (state: IState) => state.clients.projectsCount;
